import { CAMERA_TYPE } from "@/shared/state/visualization/schema/v1/three-d";

import type { BabylonState } from "../../state";

import { attachLatchedDrag } from "./attachLatchedDrag";

/**
 * Radians of camera rotation per pixel of horizontal pointer drag.
 *
 * Hand-tuned to feel responsive at the default 55° FoV without
 * overshooting on quick mouse flicks. Lower values produce a heavier
 * feel; higher values produce a more sensitive feel. Vertical drag
 * derives its sensitivity from this constant via `betaSensitivity`.
 */
export const ROTATE_SENS = 0.01;

/**
 * Vertical-axis range factor: beta's range is `π` (half of alpha's
 * `2π`), so equal pixel motion should produce half the angular delta
 * on beta to feel proportional on the camera's view range.
 */
const BETA_RANGE_FACTOR = 0.5;

/**
 * Compute the per-pixel vertical sensitivity. Combines two reductions
 * against `ROTATE_SENS`:
 *
 *   1. **Aspect ratio (`H/W`).** A typical 16:9 canvas has fewer
 *      vertical pixels than horizontal, so each vertical pixel covers
 *      a larger fraction of available screen than each horizontal
 *      pixel. Scaling by `H/W` equalizes screen-fraction-per-pixel
 *      between the axes.
 *   2. **Range mismatch (`0.5`).** Alpha sweeps `2π`, beta only `π`.
 *      Halving the rate keeps "pixels per full sweep" the same in
 *      both axes.
 *
 * Without these, an unmodified-pixel drag down feels noticeably
 * faster than the equivalent horizontal drag because both factors
 * compound to ~3× perceived sensitivity on a 16:9 screen.
 */
export function betaSensitivity(
  renderWidth: number,
  renderHeight: number,
): number {
  const aspect = renderHeight / renderWidth;
  return ROTATE_SENS * aspect * BETA_RANGE_FACTOR;
}

/**
 * Adds an orbit-drag delta into the input accumulator consumed by
 * `updateCamera`. Writes to `state.input.cameraDeltas.alpha/beta`
 * rather than mutating `babylonCamera` directly so multiple pointer
 * events between frames sum correctly. `lowerBetaLimit` /
 * `upperBetaLimit` (set on the camera at construction) clamp `beta`
 * on Babylon's `_checkInputs` path after `updateCamera` applies the
 * delta, so this function does not clamp.
 *
 * Sign convention matches Babylon's `ArcRotateCameraPointersInput`:
 * drag right rotates the camera counterclockwise around the focus
 * when viewed from above (alpha decreases); drag down decreases beta,
 * orbiting the camera up toward the north pole (β = 0, looking
 * straight down at the focus). The world slides down on screen with
 * the cursor — the "grab the world" feel orbit users are habituated
 * to.
 */
export function applyOrbitDelta(
  state: BabylonState,
  renderWidth: number,
  renderHeight: number,
  dx: number,
  dy: number,
): void {
  state.input.cameraDeltas.alpha -= dx * ROTATE_SENS;
  state.input.cameraDeltas.beta -=
    dy * betaSensitivity(renderWidth, renderHeight);
}

/**
 * Pointer-down predicate for the orbit gesture. Latches on left-click
 * with no shift/ctrl/alt modifier when a third-person camera is
 * active.
 */
export function shouldLatchOrbit(
  state: BabylonState,
  e: PointerEvent,
): boolean {
  if (e.button !== 0) {
    return false;
  }
  if (e.shiftKey || e.ctrlKey || e.altKey) {
    return false;
  }
  return state.sceneConfig.camera?.spec.type === CAMERA_TYPE.thirdPerson;
}

/**
 * Attach a left-drag-without-modifiers orbit handler to the
 * BabylonManager's canvas. Returns a cleanup that detaches listeners
 * and releases any in-flight pointer capture.
 *
 * No-op for orbit cameras: `shouldLatchOrbit` returns false, so the
 * pointerdown event is not consumed and falls through to Babylon's
 * default `ArcRotateCameraPointersInput` (still wired on the orbit
 * branch). We deliberately do not call `preventDefault`/
 * `stopPropagation` to preserve that fall-through.
 */
export function attachOrbitInput(state: BabylonState): () => void {
  return attachLatchedDrag({
    canvas: state.context.canvas,
    shouldLatch: (e) => shouldLatchOrbit(state, e),
    onMove: (_e, dx, dy) => {
      const camera = state.sceneConfig.camera;
      if (!camera || camera.spec.type !== CAMERA_TYPE.thirdPerson) {
        return;
      }
      const engine = state.context.engine;
      applyOrbitDelta(
        state,
        engine.getRenderWidth(),
        engine.getRenderHeight(),
        dx,
        dy,
      );
    },
  });
}
